import { useState, useEffect } from "react";
import {
  Bell,
  X,
  Check,
  AlertCircle,
  Info,
  Calendar,
  MessageSquare,
  Users,
  CheckCircle,
  Trash2,
  Megaphone,
} from "lucide-react";

const NotificationsPanel = ({ isOpen, onClose, notifications = [], onMarkAsRead, onClearAll, onRequestBrowserNotifications, browserNotificationPermission }) => {
  const [filter, setFilter] = useState("all"); // all, unread

  const getIcon = (type) => {
    switch (type) {
      case "alert":
        return <AlertCircle size={18} className="text-rose-500" />;
      case "info":
        return <Info size={18} className="text-blue-500" />;
      case "event":
        return <Calendar size={18} className="text-primary" />;
      case "message":
        return <MessageSquare size={18} className="text-emerald-500" />;
      case "user":
        return <Users size={18} className="text-violet-500" />;
      case "announcement":
      case "notice":
        return <Megaphone size={18} className="text-amber-500" />;
      default:
        return <Bell size={18} className="text-slate-400" />;
    }
  };

  const filteredNotifications = filter === "unread" 
    ? notifications.filter(n => !n.read)
    : notifications;

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-end sm:block" data-testid="notifications-panel">
      {/* Backdrop - cubre toda la pantalla en móvil y desktop */}
      <div 
        className="absolute inset-0 min-h-[100dvh] bg-slate-900/30 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      
      {/* Panel: en móvil ocupa casi toda la altura y ancho con márgenes; en desktop flotante */}
      <div className="relative w-[calc(100%-2rem)] max-w-sm sm:absolute sm:right-4 sm:top-4 lg:right-6 lg:top-20 flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden animate-slide-up max-h-[calc(100dvh-2rem)] sm:max-h-[min(32rem,calc(100vh-6rem))] m-4 sm:m-0">
        {/* Header */}
        <div className="p-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <h3 className="font-semibold text-slate-800 truncate">Notificaciones</h3>
            {unreadCount > 0 && (
              <span className="badge badge-primary flex-shrink-0">{unreadCount} nuevas</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors flex-shrink-0"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Filters */}
        <div className="p-2 border-b border-slate-100 flex gap-2 flex-shrink-0">
          <button
            onClick={() => setFilter("all")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
              filter === "all" 
                ? "bg-primary text-white" 
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            Todas
          </button>
          <button
            onClick={() => setFilter("unread")}
            className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-colors ${
              filter === "unread" 
                ? "bg-primary text-white" 
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            No leídas
          </button>
        </div>

        {/* Activar notificaciones del navegador (Chrome, etc.) */}
        {onRequestBrowserNotifications && browserNotificationPermission === "default" && (
          <div className="p-3 border-b border-slate-100 bg-slate-50 flex-shrink-0">
            <p className="text-xs text-slate-600 mb-2">Recibe avisos aunque no tengas la página abierta.</p>
            <button type="button" onClick={onRequestBrowserNotifications} className="text-sm font-medium text-primary hover:underline">
              Activar notificaciones del navegador
            </button>
          </div>
        )}

        {/* Notifications List - scroll oculto y adaptable a la altura disponible */}
        <div className="flex-1 min-h-0 overflow-y-auto recursos-scrollbar-hide">
          {filteredNotifications.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-slate-50 transition-colors ${
                    !notification.read ? "bg-primary/5" : ""
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      {getIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notification.read ? "font-medium text-slate-800" : "text-slate-600"}`}>
                        {notification.title}
                      </p>
                      {notification.message && (
                        <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {notification.time}
                      </p>
                    </div>
                    {!notification.read && onMarkAsRead && (
                      <button
                        onClick={() => onMarkAsRead(notification.id)}
                        className="p-1.5 hover:bg-emerald-50 text-emerald-600 rounded-lg transition-colors"
                        title="Marcar como leída"
                      >
                        <Check size={14} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <CheckCircle size={40} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500">
                {filter === "unread" 
                  ? "No hay notificaciones sin leer" 
                  : "No hay notificaciones"}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && onClearAll && (
          <div className="p-3 border-t border-slate-100 flex-shrink-0">
            <button
              onClick={onClearAll}
              className="w-full py-2.5 text-sm text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Trash2 size={14} />
              Limpiar todas
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPanel;
